import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Activity as ActivityIcon } from "lucide-react";
import type { AdminActivity } from "@lumika/shared";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { BarChart, BreakdownBars } from "../../components/Charts";
import { formatDateTime, humanize } from "../../lib/format";

/**
 * Admin activity feed — an actor-first analysis view. It leads with
 * who has been working this dealership and how busy it has been, then
 * lists the individual actions. Operator and entity-type filters drive
 * the whole page (metrics, charts, and table together).
 */
export function DealershipActivityPage() {
  const { dealershipId } = useParams();
  const [actorId, setActorId] = useState("all");
  const [entityType, setEntityType] = useState("all");

  const activitiesQuery = useQuery({
    queryKey: ["admin-activities", dealershipId],
    queryFn: () => adminApi.listAdminActivities(dealershipId, 250),
    enabled: Boolean(dealershipId),
  });

  const items = useMemo(
    () => activitiesQuery.data ?? [],
    [activitiesQuery.data],
  );

  // Distinct operators / entity types power the two filter dropdowns.
  const actors = useMemo(() => {
    const byId = new Map<string, string>();
    for (const a of items) byId.set(a.adminId, a.adminName);
    return [...byId.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [items]);
  const entityTypes = useMemo(() => {
    const set = new Set<string>();
    for (const a of items) if (a.entityType) set.add(a.entityType);
    return [...set].sort();
  }, [items]);

  // Filters drive the whole page — metrics, charts, and the table all
  // reflect the current operator + entity selection.
  const filtered = useMemo(
    () =>
      items.filter(
        (a) =>
          (actorId === "all" || a.adminId === actorId) &&
          (entityType === "all" || a.entityType === entityType),
      ),
    [items, actorId, entityType],
  );

  // Actions per day, oldest→newest (the feed itself is newest-first).
  // Zero-activity days are filled in so bursty attention doesn't read
  // as continuous daily activity on the chart.
  const byDay = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of filtered) {
      const day = a.createdAt.slice(0, 10);
      counts.set(day, (counts.get(day) ?? 0) + 1);
    }
    if (counts.size === 0) return [];
    const days = [...counts.keys()].sort();
    const out: { label: string; value: number }[] = [];
    const last = new Date(`${days[days.length - 1]!}T00:00:00Z`);
    // Clamp the fill window to ~60 days before the newest day — the
    // 250-item feed has no date floor, so one ancient activity would
    // otherwise emit hundreds of bars.
    const lookback = new Date(last);
    lookback.setUTCDate(lookback.getUTCDate() - 59);
    const cursor = new Date(
      Math.max(new Date(`${days[0]!}T00:00:00Z`).getTime(), lookback.getTime()),
    );
    while (cursor.getTime() <= last.getTime()) {
      const day = cursor.toISOString().slice(0, 10);
      out.push({ label: day.slice(5), value: counts.get(day) ?? 0 });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return out;
  }, [filtered]);

  // Actions per operator — the "who is doing the work" view.
  const byActor = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of filtered) {
      counts.set(a.adminName, (counts.get(a.adminName) ?? 0) + 1);
    }
    return [...counts.entries()].map(([label, value]) => ({ label, value }));
  }, [filtered]);

  const operatorCount = new Set(filtered.map((a) => a.adminId)).size;
  const entityTypeCount = new Set(
    filtered.map((a) => a.entityType).filter(Boolean),
  ).size;
  const lastActivity = filtered[0]?.createdAt ?? null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Activity"
        title="Admin activity"
        description="Who has been working this dealership, and what they changed."
      />

      {activitiesQuery.isLoading ? (
        <PageLoading message="Loading activity…" />
      ) : activitiesQuery.isError ? (
        <PageError
          title="Couldn't load activity"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<ActivityIcon className="h-5 w-5" />}
          title="No activity yet"
          description="Actions taken on this dealership will be logged here."
        />
      ) : (
        <>
          {/* Filters — drive the metrics, charts, and table together. */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-56">
              <Select value={actorId} onValueChange={setActorId}>
                <SelectTrigger aria-label="Filter by operator">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All operators</SelectItem>
                  {actors.map(([id, name]) => (
                    <SelectItem key={id} value={id}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger aria-label="Filter by entity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All entities</SelectItem>
                  {entityTypes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {humanize(t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Actions" value={filtered.length.toLocaleString("en-US")} />
            <Metric label="Operators" value={String(operatorCount)} />
            <Metric label="Entity types" value={String(entityTypeCount)} />
            <Metric
              label="Last activity"
              value={lastActivity ? formatDateTime(lastActivity) : "—"}
            />
          </div>

          <Section
            title="Activity by day"
            description="How busy this dealership has been."
          >
            <BarChart
              data={byDay}
              formatValue={(n) => `${n} action${n === 1 ? "" : "s"}`}
              emptyText="No activity for this filter."
            />
          </Section>

          <Section
            title="Most active operators"
            description="Who is doing the work, ranked by number of actions."
          >
            <BreakdownBars
              data={byActor}
              formatValue={(n) => `${n} action${n === 1 ? "" : "s"}`}
              emptyText="No activity for this filter."
            />
          </Section>

          <Section title={`${filtered.length} actions`}>
            {filtered.length === 0 ? (
              <EmptyState
                icon={<ActivityIcon className="h-5 w-5" />}
                title="Nothing matches this filter"
                description="Try a different operator or entity."
              />
            ) : (
              <>
                {/* Desktop: full activity table. */}
                <TableBleed className="-mt-6 hidden border-b md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operator</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>When</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.slice(0, 100).map((activity) => {
                        const { action, target } = describeActivity(activity);
                        return (
                          <TableRow key={activity.id}>
                            <TableCell className="font-medium">
                              {activity.adminName}
                            </TableCell>
                            <TableCell>{action}</TableCell>
                            <TableCell>
                              {target ? (
                                <Badge variant="secondary">{target}</Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {formatDateTime(activity.createdAt)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableBleed>
                {/* Mobile: condensed touch list with the same data hierarchy. */}
                <MobileList>
                  {filtered.slice(0, 100).map((activity) => {
                    const { action, target } = describeActivity(activity);
                    return (
                      <MobileListRow
                        key={activity.id}
                        primary={activity.adminName}
                        secondary={action}
                        meta={
                          target ? (
                            <Badge variant="secondary">{target}</Badge>
                          ) : undefined
                        }
                        trailing={
                          <span className="whitespace-nowrap text-xs text-muted-foreground">
                            {formatDateTime(activity.createdAt)}
                          </span>
                        }
                      />
                    );
                  })}
                </MobileList>
                {filtered.length > 100 && (
                  <p className="pt-2 text-xs text-muted-foreground">
                    Showing the first 100 of {filtered.length} actions — refine the
                    filters to narrow them down.
                  </p>
                )}
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

/**
 * A readable "what happened" pair. Prefer the structured
 * `entityAction`; fall back to the raw `type` code when an activity
 * has none. `target` is the entity the action touched, if any.
 */
function describeActivity(a: AdminActivity): {
  action: string;
  target: string | null;
} {
  return {
    action: a.entityAction ? humanize(a.entityAction) : humanize(a.type),
    target: a.entityType ? humanize(a.entityType) : null,
  };
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}
